import { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Switch, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CircularLoading } from "../utils/loading";
import { openGlobalSnack } from "../utils/snack";
import { formatLocalCurrency } from "../../utils/currency";
import { formatDateWithMin } from "../../utils/date";
import { TableFeeId, getTableNameByID } from "../dashboard/types";
import { CashSummary, CashSummaryCards, EntriesTable } from "./cashpanel";
import { HS_ADMIN, PRODUCT_LABEL, centsToReais, hsRequest, newIdempotencyKey, reaisInputToCents } from "./api";

function useLoad<T>(path: string, query?: Record<string, string | undefined>) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const key = JSON.stringify(query || {});
    const reload = useCallback(async () => {
        setLoading(true);
        try { setData(await hsRequest<T>('GET', path, undefined, query)); }
        catch (e: any) { openGlobalSnack(e.message, 'error'); }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, key]);
    useEffect(() => { reload(); }, [reload]);
    return { data, loading, reload };
}
const pct = (bp: number) => `${(bp / 100).toFixed(2).replace(/\.00$/, '')}%`;
const ruleReasonLabel = (reasons: Record<string, string> | undefined, r: string) => reasons?.[r] || r;

/* ------------------------------ Colaboradores e caixa ------------------------------ */

export function CollaboratorsPanel() {
    const { data, loading, reload } = useLoad<{ users: any[] }>(`${HS_ADMIN}/users`);
    const [cashes, setCashes] = useState<Record<number, CashSummary>>({});
    const [open, setOpen] = useState<any>(null);
    const [creating, setCreating] = useState(false);
    const [secret, setSecret] = useState<{ title: string; email: string; password: string } | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await hsRequest<{ collaborators: any[] }>('GET', `${HS_ADMIN}/collaborators`);
                const m: Record<number, CashSummary> = {};
                r.collaborators.forEach((c) => { m[c.id] = c.cash; });
                setCashes(m);
            } catch (e: any) { openGlobalSnack(e.message, 'error'); }
        })();
    }, [data]);

    async function toggle(u: any, v: boolean) {
        try {
            await hsRequest('PUT', `${HS_ADMIN}/collaborators/${u.id}/permission`, { canHumanSales: v });
            openGlobalSnack(v ? 'Atendimento liberado.' : 'Atendimento removido.', 'success');
            reload();
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
    }
    async function resetPassword(u: any) {
        if (!window.confirm(`Gerar uma nova senha para ${u.name}? A senha atual deixa de funcionar.`)) return;
        try {
            const r = await hsRequest<{ password: string }>('PUT', `${HS_ADMIN}/users/${u.id}/password`, {});
            setSecret({ title: `Nova senha de ${u.name}`, email: u.email, password: r.password });
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
    }
    if (loading && !data) return <CircularLoading />;
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button variant="contained" onClick={() => setCreating(true)}>Novo atendente</Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Quem está com a chave ligada vê a fila de leads. O saldo disponível é o que já passou da carência e pode ser pago.
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Usuário</TableCell><TableCell>Atende?</TableCell><TableCell align="right">Disponível</TableCell>
                        <TableCell align="right">Pendente</TableCell><TableCell align="right">Total</TableCell><TableCell />
                    </TableRow></TableHead>
                    <TableBody>
                        {(data?.users || []).map((u) => {
                            const c = cashes[u.id];
                            return (
                                <TableRow key={u.id} hover>
                                    <TableCell>{u.name}<Typography variant="caption" display="block" color="text.secondary">{u.email}</Typography></TableCell>
                                    <TableCell><Switch checked={u.canHumanSales} onChange={(_, v) => toggle(u, v)} /></TableCell>
                                    <TableCell align="right">{c ? formatLocalCurrency(centsToReais(c.available)) : '—'}</TableCell>
                                    <TableCell align="right">{c ? formatLocalCurrency(centsToReais(c.pending)) : '—'}</TableCell>
                                    <TableCell align="right">{c ? formatLocalCurrency(centsToReais(c.total)) : '—'}</TableCell>
                                    <TableCell align="right"><Button size="small" variant="outlined" onClick={() => setOpen(u)}>Caixa</Button>
                                        <Button size="small" sx={{ ml: 1 }} onClick={() => resetPassword(u)}>Nova senha</Button></TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {open ? <CollaboratorCashDialog user={open} onClose={() => { setOpen(null); reload(); }} /> : null}
            {creating ? <NewAttendantDialog onClose={() => setCreating(false)} onCreated={(u, password) => {
                setCreating(false); reload();
                if (password) setSecret({ title: `Atendente ${u.name} cadastrado`, email: u.email, password });
                else openGlobalSnack('Atendente cadastrado.', 'success');
            }} /> : null}
            {secret ? <SecretDialog secret={secret} onClose={() => setSecret(null)} /> : null}
        </Box>
    );
}

function NewAttendantDialog({ onClose, onCreated }: { onClose: () => any; onCreated: (u: any, password?: string) => any }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);

    async function submit() {
        if (name.trim().length < 2) { openGlobalSnack('Informe o nome.', 'error'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { openGlobalSnack('E-mail inválido.', 'error'); return; }
        if (password && password.length < 8) { openGlobalSnack('A senha precisa ter ao menos 8 caracteres.', 'error'); return; }
        setBusy(true);
        try {
            const r = await hsRequest<{ user: any; password?: string }>('POST', `${HS_ADMIN}/users`, { name, email, password });
            onCreated(r.user, r.password);
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
        setBusy(false);
    }
    return (
        <Dialog open fullWidth maxWidth="xs" onClose={busy ? undefined : onClose}>
            <DialogTitle>Novo atendente</DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField size="small" label="Nome" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                <TextField size="small" label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <TextField size="small" label="Senha (opcional)" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    helperText="Deixe em branco para gerar uma senha aleatória de 16 caracteres." />
                <Typography variant="caption" color="text.secondary">O atendente já é criado com o atendimento liberado.</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancelar</Button>
                <LoadingButton variant="contained" loading={busy} onClick={submit}>Cadastrar</LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

/** Mostra a senha gerada uma única vez: o servidor não guarda nem devolve de novo. */
function SecretDialog({ secret, onClose }: { secret: { title: string; email: string; password: string }; onClose: () => any }) {
    async function copy() {
        try { await navigator.clipboard.writeText(secret.password); openGlobalSnack('Senha copiada.', 'success'); }
        catch { openGlobalSnack('Não foi possível copiar; selecione e copie manualmente.', 'error'); }
    }
    return (
        <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
            <DialogTitle>{secret.title}</DialogTitle>
            <DialogContent dividers>
                <Alert severity="warning" sx={{ mb: 2 }}>Anote agora: por segurança esta senha não será exibida novamente.</Alert>
                <Typography variant="caption" color="text.secondary">Login</Typography>
                <Typography sx={{ mb: 1 }}>{secret.email}</Typography>
                <Typography variant="caption" color="text.secondary">Senha</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 20, userSelect: 'all' }}>{secret.password}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={copy}>Copiar senha</Button>
                <Button variant="contained" onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

function CollaboratorCashDialog({ user, onClose }: { user: any; onClose: () => any }) {
    const { data, loading, reload } = useLoad<{ summary: CashSummary; entries: any[] }>(`${HS_ADMIN}/collaborators/${user.id}/cash`);
    const [mode, setMode] = useState<'add' | 'remove' | 'pay'>('add');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    // Uma chave por tentativa: reenviar o MESMO formulário (clique duplo, rede lenta) nunca lança duas vezes.
    const [idem, setIdem] = useState(newIdempotencyKey());

    async function submit() {
        const cents = reaisInputToCents(amount);
        if (!Number.isFinite(cents) || cents <= 0) { openGlobalSnack('Informe um valor válido.', 'error'); return; }
        if (mode !== 'pay' && reason.trim().length < 5) { openGlobalSnack('Informe o motivo (mín. 5 caracteres).', 'error'); return; }
        const label = mode === 'add' ? 'ADICIONAR' : mode === 'remove' ? 'REMOVER' : 'REGISTRAR PAGAMENTO de';
        if (!window.confirm(`Confirma ${label} ${formatLocalCurrency(centsToReais(cents))} para ${user.name}?`)) return;
        setBusy(true);
        try {
            if (mode === 'pay') await hsRequest('POST', `${HS_ADMIN}/collaborators/${user.id}/cash/pay`, { amountCents: cents, note: reason, idempotencyKey: idem });
            else await hsRequest('POST', `${HS_ADMIN}/collaborators/${user.id}/cash/adjust`, { amountCents: mode === 'add' ? cents : -cents, reason, idempotencyKey: idem });
            openGlobalSnack('Lançamento registrado.', 'success');
            setAmount(''); setReason(''); setIdem(newIdempotencyKey()); reload();
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
        setBusy(false);
    }
    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>Caixa de {user.name}</DialogTitle>
            <DialogContent dividers>
                {loading && !data ? <CircularLoading /> : data ? <CashSummaryCards summary={data.summary} /> : null}
                <Paper variant="outlined" sx={{ p: 2, my: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <TextField select size="small" label="Operação" value={mode} onChange={(e) => { setMode(e.target.value as any); setIdem(newIdempotencyKey()); }} sx={{ minWidth: 190 }}>
                            <MenuItem value="add">Adicionar valor</MenuItem>
                            <MenuItem value="remove">Remover valor</MenuItem>
                            <MenuItem value="pay">Registrar pagamento</MenuItem>
                        </TextField>
                        <TextField size="small" label="Valor (R$)" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" sx={{ width: 140 }} />
                        <TextField size="small" label={mode === 'pay' ? 'Observação (ex.: PIX enviado)' : 'Motivo (obrigatório)'} value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
                        <LoadingButton variant="contained" loading={busy} onClick={submit}>Registrar</LoadingButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {mode === 'pay' ? 'O pagamento só registra a saída do caixa (o PIX é feito por fora) e não passa do saldo disponível.' : 'Lançamentos não são editados nem apagados: um erro se corrige com um novo lançamento. Tudo fica no log de auditoria.'}
                    </Typography>
                </Paper>
                {data ? <EntriesTable entries={data.entries} /> : null}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
        </Dialog>
    );
}

/* ------------------------------ Comissão por estado do lead ------------------------------ */

export function RulesPanel() {
    const { data, loading, reload } = useLoad<{ rules: any[]; reasons: Record<string, string> }>(`${HS_ADMIN}/rules`);
    const users = useLoad<{ users: any[] }>(`${HS_ADMIN}/users`);
    const [form, setForm] = useState({ product: 'clt', reason: '*', userId: '', percent: '' });
    const [busy, setBusy] = useState(false);

    async function save(r: { product: string; reason: string; userId?: number; percentBp: number; active?: boolean }) {
        setBusy(true);
        try { await hsRequest('PUT', `${HS_ADMIN}/rules`, r); openGlobalSnack('Regra salva.', 'success'); reload(); }
        catch (e: any) { openGlobalSnack(e.message, 'error'); }
        setBusy(false);
    }
    function add() {
        const p = Number(form.percent.replace(',', '.'));
        if (!Number.isFinite(p) || p < 0 || p > 100) { openGlobalSnack('Percentual deve estar entre 0 e 100.', 'error'); return; }
        save({ product: form.product, reason: form.reason, userId: form.userId ? Number(form.userId) : undefined, percentBp: Math.round(p * 100) }).then(() => setForm({ ...form, percent: '' }));
    }
    const userName = (id: number) => users.data?.users.find((u) => u.id === id)?.name || `#${id}`;
    if (loading && !data) return <CircularLoading />;
    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                O percentual é aplicado sobre a <b>comissão da casa</b> e fica travado no momento em que o atendente pega o lead (mudar a regra depois não altera leads já pegos).
                Prioridade: colaborador + situação → situação → padrão do colaborador → padrão global. Sem nenhuma regra, vale 50%.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField size="small" select label="Produto" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} sx={{ width: 110 }}>
                    <MenuItem value="clt">CLT</MenuItem><MenuItem value="fgts">FGTS</MenuItem>
                </TextField>
                <TextField size="small" select label="Situação do lead" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} sx={{ minWidth: 240 }}>
                    {Object.entries(data?.reasons || {}).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
                <TextField size="small" select label="Colaborador" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} sx={{ minWidth: 200 }}>
                    <MenuItem value="">Todos (regra global)</MenuItem>
                    {(users.data?.users || []).filter((u) => u.canHumanSales).map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
                </TextField>
                <TextField size="small" label="% da comissão" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} sx={{ width: 130 }} />
                <LoadingButton variant="contained" loading={busy} onClick={add}>Salvar regra</LoadingButton>
            </Paper>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead><TableRow><TableCell>Produto</TableCell><TableCell>Situação</TableCell><TableCell>Vale para</TableCell><TableCell align="right">Percentual</TableCell><TableCell>Ativa</TableCell></TableRow></TableHead>
                    <TableBody>
                        {(data?.rules || []).map((r) => (
                            <TableRow key={r.id}>
                                <TableCell>{PRODUCT_LABEL[r.product] || r.product}</TableCell>
                                <TableCell>{ruleReasonLabel(data?.reasons, r.reason)}</TableCell>
                                <TableCell>{r.user_key ? userName(r.user_key) : 'Todos'}</TableCell>
                                <TableCell align="right">{pct(r.percent_bp)}</TableCell>
                                <TableCell><Switch checked={r.active} disabled={busy} onChange={(_, v) => save({ product: r.product, reason: r.reason, userId: r.user_key || undefined, percentBp: r.percent_bp, active: v })} /></TableCell>
                            </TableRow>
                        ))}
                        {data && data.rules.length === 0 ? <TableRow><TableCell colSpan={5} align="center">Nenhuma regra cadastrada (vale o padrão de 50%).</TableCell></TableRow> : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

/* ------------------------------ Taxas de tabela da casa ------------------------------ */

const V8_TIERS = ['46', '36', '24', '18', '12', '10', '8', '6', '0'];
const SUMA_TIERS = ['36', '30', '24', '18', '12'];
type FgtsTableInfo = { id: string; provider: string | null; label: string | null; total: number };
function keyOptions(provider: string, extra: FgtsTableInfo[] = []): { value: string; label: string }[] {
    if (provider === 'fgts') {
        const base = Object.values(TableFeeId).map((id) => ({ value: id as string, label: `${getTableNameByID(id)} (${id.slice(0, 8)}…)` }));
        const seen = new Set(base.map((o) => o.value));
        const more = extra.filter((t) => !seen.has(t.id)).map((t) => ({ value: t.id, label: `${t.label ? t.label : 'Sem nome'}${t.provider ? ' • ' + t.provider : ''} (${t.id.slice(0, 8)}…) • ${t.total} prop.` }));
        return [...base, ...more];
    }
    if (provider === 'v8') return V8_TIERS.flatMap((t) => [{ value: `${t}:s`, label: `${t === '0' ? 'até 5' : t + '+'} parcelas • com seguro` }, { value: `${t}:n`, label: `${t === '0' ? 'até 5' : t + '+'} parcelas • sem seguro` }]);
    return [{ value: 'flat:n', label: 'Sem seguro (flat)' }, ...SUMA_TIERS.map((t) => ({ value: `${t}:s`, label: `${t}+ parcelas • com seguro` }))];
}

export function TableRatesPanel() {
    const { data, loading, reload } = useLoad<{ rates: any[] }>(`${HS_ADMIN}/table-rates`);
    const { data: tablesData, reload: reloadTables } = useLoad<{ tables: FgtsTableInfo[] }>(`${HS_ADMIN}/fgts-tables`);
    const extraTables = tablesData?.tables || [];
    const [form, setForm] = useState({ provider: 'fgts', tableKey: '', rate: '', validFrom: '' });
    const [busy, setBusy] = useState(false);
    const [newTable, setNewTable] = useState<{ open: boolean; id: string; label: string }>({ open: false, id: '', label: '' });

    async function addTable() {
        const id = newTable.id.trim().toLowerCase();
        if (!/^[0-9a-f-]{8,40}$/.test(id)) { openGlobalSnack('Informe o id (UUID) da tabela.', 'error'); return; }
        try {
            if (newTable.label.trim()) await hsRequest('POST', `${HS_ADMIN}/fgts-tables`, { id, label: newTable.label.trim() });
            setForm({ ...form, provider: 'fgts', tableKey: id });
            setNewTable({ open: false, id: '', label: '' });
            reloadTables();
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
    }

    async function create() {
        const r = Number(form.rate.replace(',', '.'));
        if (!form.tableKey) { openGlobalSnack('Escolha a tabela/faixa.', 'error'); return; }
        if (!Number.isFinite(r) || r < 0 || r > 100) { openGlobalSnack('Taxa deve estar entre 0 e 100%.', 'error'); return; }
        if (!window.confirm(`Nova taxa de ${r}% para ${form.provider.toUpperCase()} / ${form.tableKey}. Vale para propostas criadas a partir de agora; as já criadas não mudam. Confirmar?`)) return;
        setBusy(true);
        try {
            await hsRequest('POST', `${HS_ADMIN}/table-rates`, { provider: form.provider, tableKey: form.tableKey, rate: r / 100, validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : undefined });
            openGlobalSnack('Taxa registrada.', 'success'); setForm({ ...form, rate: '' }); reload();
        } catch (e: any) { openGlobalSnack(e.message, 'error'); }
        setBusy(false);
    }
    async function deactivate(id: number) {
        if (!window.confirm('Desativar esta taxa? Volta a valer a taxa anterior ou o padrão do sistema.')) return;
        try { await hsRequest('DELETE', `${HS_ADMIN}/table-rates/${id}`); reload(); } catch (e: any) { openGlobalSnack(e.message, 'error'); }
    }
    const keyLabel = (provider: string, key: string) => keyOptions(provider, extraTables).find((o) => o.value === key)?.label || key;
    if (loading && !data) return <CircularLoading />;
    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Taxa que a <b>casa</b> recebe por tabela. Cada alteração é uma nova versão: a comissão fica gravada na proposta no momento da criação, então mudar a taxa nunca altera o passado.
                Sem nenhuma taxa cadastrada vale o padrão do sistema.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField size="small" select label="Produto/banco" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value, tableKey: '' })} sx={{ width: 150 }}>
                    <MenuItem value="fgts">FGTS</MenuItem><MenuItem value="v8">CLT V8</MenuItem><MenuItem value="suma">CLT Soma</MenuItem>
                </TextField>
                <TextField size="small" select label="Tabela / faixa" value={form.tableKey} onChange={(e) => setForm({ ...form, tableKey: e.target.value })} sx={{ minWidth: 260 }}>
                    {keyOptions(form.provider, extraTables).concat(form.provider === 'fgts' && form.tableKey && !keyOptions('fgts', extraTables).some((o) => o.value === form.tableKey) ? [{ value: form.tableKey, label: `${form.tableKey.slice(0, 8)}… (nova)` }] : []).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                {form.provider === 'fgts' ? <Button size="small" onClick={() => setNewTable({ ...newTable, open: !newTable.open })}>+ Adicionar tabela</Button> : null}
                <TextField size="small" label="Taxa (%)" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} sx={{ width: 110 }} />
                <TextField size="small" type="datetime-local" label="Vale a partir de (opcional)" InputLabelProps={{ shrink: true }} value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
                <LoadingButton variant="contained" loading={busy} onClick={create}>Registrar taxa</LoadingButton>
                {newTable.open && form.provider === 'fgts' ? (
                    <Box sx={{ width: '100%', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <TextField size="small" label="Id da tabela (UUID)" value={newTable.id} onChange={(e) => setNewTable({ ...newTable, id: e.target.value })} sx={{ minWidth: 330 }} />
                        <TextField size="small" label="Nome (ex.: podium)" value={newTable.label} onChange={(e) => setNewTable({ ...newTable, label: e.target.value })} sx={{ width: 180 }} />
                        <Button variant="outlined" onClick={addTable}>Adicionar</Button>
                    </Box>
                ) : null}
            </Paper>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead><TableRow><TableCell>Produto</TableCell><TableCell>Tabela / faixa</TableCell><TableCell align="right">Taxa</TableCell><TableCell>Vigente desde</TableCell><TableCell>Situação</TableCell><TableCell /></TableRow></TableHead>
                    <TableBody>
                        {(data?.rates || []).map((r) => (
                            <TableRow key={r.id}>
                                <TableCell>{r.provider === 'fgts' ? 'FGTS' : r.provider === 'v8' ? 'CLT V8' : 'CLT Soma'}</TableCell>
                                <TableCell>{keyLabel(r.provider, r.table_key)}</TableCell>
                                <TableCell align="right">{(r.rate * 100).toFixed(3).replace(/\.?0+$/, '')}%</TableCell>
                                <TableCell>{formatDateWithMin(r.valid_from)}</TableCell>
                                <TableCell>{r.active ? <Chip size="small" color="success" label="Ativa" /> : <Chip size="small" label="Desativada" />}</TableCell>
                                <TableCell align="right">{r.active ? <Button size="small" color="error" onClick={() => deactivate(r.id)}>Desativar</Button> : null}</TableCell>
                            </TableRow>
                        ))}
                        {data && data.rates.length === 0 ? <TableRow><TableCell colSpan={6} align="center">Nenhuma taxa personalizada: valem os padrões do sistema.</TableCell></TableRow> : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

/* ------------------------------ Leads (visão geral) ------------------------------ */

const STATUS_LABEL: Record<string, string> = { queued: 'Na fila', claimed: 'Em atendimento', closed_won: 'Venda', closed_lost: 'Perdido', expired: 'Expirado', transferred: 'Transferido ao robô' };

export function LeadsOverviewPanel() {
    const [status, setStatus] = useState('');
    const { data, loading, reload } = useLoad<{ leads: any[] }>(`${HS_ADMIN}/leads`, { status: status || undefined });
    const users = useLoad<{ users: any[] }>(`${HS_ADMIN}/users`);
    const [events, setEvents] = useState<{ lead: any; list: any[] } | null>(null);
    const name = (id: number | null) => (id ? users.data?.users.find((u) => u.id === id)?.name || `#${id}` : '—');

    async function showEvents(l: any) {
        try { const r = await hsRequest<{ events: any[] }>('GET', `${HS_ADMIN}/leads/${l.id}/events`); setEvents({ lead: l, list: r.events }); }
        catch (e: any) { openGlobalSnack(e.message, 'error'); }
    }
    async function release(l: any) {
        if (!window.confirm(`Tirar o lead #${l.id} de ${name(l.claimed_by)} e devolver à fila?`)) return;
        try { await hsRequest('POST', `${HS_ADMIN}/leads/${l.id}/release`); openGlobalSnack('Lead devolvido.', 'success'); reload(); }
        catch (e: any) { openGlobalSnack(e.message, 'error'); }
    }
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField size="small" select label="Situação" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 190 }}>
                    <MenuItem value="">Todas</MenuItem>
                    {Object.entries(STATUS_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
                <Button onClick={reload}>Atualizar</Button>
            </Box>
            {loading && !data ? <CircularLoading /> : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead><TableRow><TableCell>#</TableCell><TableCell>Produto</TableCell><TableCell>Motivo</TableCell><TableCell>Situação</TableCell><TableCell>Atendente</TableCell><TableCell>%</TableCell><TableCell>Msgs</TableCell><TableCell>Atualizado</TableCell><TableCell /></TableRow></TableHead>
                        <TableBody>
                            {(data?.leads || []).map((l) => (
                                <TableRow key={l.id} hover>
                                    <TableCell>{l.id}</TableCell>
                                    <TableCell>{PRODUCT_LABEL[l.product] || l.product}</TableCell>
                                    <TableCell>{l.reason}</TableCell>
                                    <TableCell><Chip size="small" label={STATUS_LABEL[l.status] || l.status} color={l.status === 'closed_won' ? 'success' : l.status === 'claimed' ? 'primary' : 'default'} /></TableCell>
                                    <TableCell>{name(l.claimed_by)}</TableCell>
                                    <TableCell>{l.rule_percent != null ? pct(l.rule_percent) : '—'}</TableCell>
                                    <TableCell>{l.messages_sent}</TableCell>
                                    <TableCell>{formatDateWithMin(l.updated_at)}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => showEvents(l)}>Histórico</Button>
                                        {l.status === 'claimed' ? <Button size="small" color="error" onClick={() => release(l)}>Tirar</Button> : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {data && data.leads.length === 0 ? <TableRow><TableCell colSpan={9} align="center">Nenhum lead.</TableCell></TableRow> : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {events ? (
                <Dialog open fullWidth maxWidth="sm" onClose={() => setEvents(null)}>
                    <DialogTitle>Lead #{events.lead.id} — linha do tempo</DialogTitle>
                    <DialogContent dividers>
                        {events.list.map((ev) => (
                            <Box key={ev.id} sx={{ mb: 1 }}>
                                <b>{ev.type}</b> <Typography component="span" variant="caption" color="text.secondary">{formatDateWithMin(ev.created_at)} • {ev.user_id ? name(ev.user_id) : 'sistema'}</Typography>
                                {ev.data ? <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>{ev.data}</Typography> : null}
                            </Box>
                        ))}
                    </DialogContent>
                    <DialogActions><Button onClick={() => setEvents(null)}>Fechar</Button></DialogActions>
                </Dialog>
            ) : null}
        </Box>
    );
}

/* ------------------------------ Configurações e auditoria ------------------------------ */

const SETTING_LABEL: Record<string, string> = {
    enabled: 'Programa ativo (1 = ligado, 0 = desligado)',
    grace_days: 'Carência da comissão (dias após a proposta paga)',
    attribution_days: 'Janela para a venda contar para o atendente (dias)',
    claim_timeout_min: 'Prazo para o 1º contato após pegar o lead (min)',
    max_active_per_user: 'Conversas ativas por atendente',
    max_portfolio_per_user: 'Carteira total por atendente (inclui retornos)',
    proposal_stuck_minutes: 'Espera antes de recuperar proposta parada (min)',
    auth_failed_grace_minutes: 'Espera após falha de autorização (min)',
    cooldown_days: 'Dias até um lead trabalhado voltar à fila',
    lead_max_age_hours: 'Idade máxima do chat para entrar na fila (h)',
    lead_stale_max_hours: 'Idade máxima do lead fora da janela de 24h, atendido por contato externo (h)',
    grace_minutes_default: 'Inatividade mínima antes de entrar na fila (min)',
};

export function SettingsPanel() {
    const { data, loading, reload } = useLoad<{ settings: Record<string, string> }>(`${HS_ADMIN}/settings`);
    const [edit, setEdit] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState('');
    async function save(key: string) {
        setBusy(key);
        try { await hsRequest('PUT', `${HS_ADMIN}/settings`, { key, value: edit[key] }); openGlobalSnack('Salvo.', 'success'); setEdit((e) => { const n = { ...e }; delete n[key]; return n; }); reload(); }
        catch (e: any) { openGlobalSnack(e.message, 'error'); }
        setBusy('');
    }
    if (loading && !data) return <CircularLoading />;
    return (
        <Box sx={{ maxWidth: 640 }}>
            {Object.entries(SETTING_LABEL).map(([key, label]) => (
                <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                    <TextField size="small" fullWidth label={label} value={edit[key] ?? data?.settings[key] ?? ''} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} />
                    <LoadingButton variant="outlined" disabled={edit[key] === undefined || edit[key] === data?.settings[key]} loading={busy === key} onClick={() => save(key)}>Salvar</LoadingButton>
                </Box>
            ))}
        </Box>
    );
}

export function AuditPanel() {
    const [prefix, setPrefix] = useState('');
    const { data, loading, reload } = useLoad<{ logs: any[] }>(`${HS_ADMIN}/audit`, { action: prefix || undefined });
    const users = useLoad<{ users: any[] }>(`${HS_ADMIN}/users`);
    const name = (id: number | null) => (id ? users.data?.users.find((u) => u.id === id)?.name || `#${id}` : 'sistema');
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField size="small" select label="Tipo" value={prefix} onChange={(e) => setPrefix(e.target.value)} sx={{ minWidth: 220 }}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="cash">Caixa (ajustes e pagamentos)</MenuItem>
                    <MenuItem value="table_rate">Taxas de tabela</MenuItem>
                    <MenuItem value="rule">Percentuais por estado</MenuItem>
                    <MenuItem value="settings">Configurações</MenuItem>
                    <MenuItem value="collaborator">Permissões</MenuItem>
                    <MenuItem value="lead">Leads</MenuItem>
                </TextField>
                <Button onClick={reload}>Atualizar</Button>
            </Box>
            {loading && !data ? <CircularLoading /> : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead><TableRow><TableCell>Quando</TableCell><TableCell>Quem</TableCell><TableCell>Ação</TableCell><TableCell>Alvo</TableCell><TableCell>Antes</TableCell><TableCell>Depois</TableCell></TableRow></TableHead>
                        <TableBody>
                            {(data?.logs || []).map((l) => (
                                <TableRow key={l.id}>
                                    <TableCell>{formatDateWithMin(l.created_at)}</TableCell><TableCell>{name(l.user_id)}</TableCell>
                                    <TableCell>{l.action}</TableCell><TableCell>{l.target}</TableCell>
                                    <TableCell sx={{ maxWidth: 200, wordBreak: 'break-all' }}>{l.before || '—'}</TableCell>
                                    <TableCell sx={{ maxWidth: 240, wordBreak: 'break-all' }}>{l.after || '—'}</TableCell>
                                </TableRow>
                            ))}
                            {data && data.logs.length === 0 ? <TableRow><TableCell colSpan={6} align="center">Sem registros.</TableCell></TableRow> : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
